import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { BarChartIcon, BankNoteIcon, CalendarIcon, HomeIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useWorkItemStatusCounts } from '../../hooks/queries/useWorkItemStatusCounts';
import { useFeatureGate } from '../../hooks/useFeatureGates';
import { PATHS } from '../../paths';
import { useAuthState } from '../../providers/AuthProvider';
import { usePathWithDateRange } from '../../providers/DateRangeProvider';
import { useSidePanel } from '../../providers/SidePanelProvider';
import EntitySearchModal from '../search/EntitySearchModal';

import { MAIN_SIDEBAR_WIDTH_PX } from './dimensions';
import FullSidebarContent from './FullSidebarContent';
import IconRailSidebar from './IconRailSidebar';
import MobileNavDrawer from './MobileNavDrawer';
import MobileTopBar from './MobileTopBar';

import type { NavItemType } from './config';
import type { LoggedInUser } from '../../providers/AuthProvider';

const ALERT_COUNT_POLL_INTERVAL_MS = 60_000;

interface IBaseSidebarProps {
  user: LoggedInUser;
  /** URL of the currently active item. */
  activeUrl?: string;
  /** List of items to display. */
  items: NavItemType[];
  /** List of footer items to display. */
  footerItems?: NavItemType[];
  /** Whether to show the account card. */
  showAccountCard?: boolean;
  /**
   * Whether to render the alerts bell. Drives visibility across all three
   * sidebar presentations and the mobile top bar together.
   */
  showAlertsBell?: boolean;
  /** Unread alert count shown on the bell's pip. */
  alertsUnreadCount?: number;
  /**
   * Whether the full Notification Center is showing in the `xl+` side column.
   * The `/alerts` route redirects away as soon as it opens the center, so the
   * pathname alone would never mark the bell current.
   */
  isAlertsCenterOpen?: boolean;
  /** Opens the Notification Center in place; see {@link NotificationBellButton}. */
  onOpenAlerts?: () => void;
  /** Additional CSS classes to apply to the desktop fixed sidebar. */
  className?: string;
  /**
   * The router's `location.key`, so the drawer closes on every navigation and
   * not only on a pathname change.
   */
  navigationKey?: string;
}

/**
 * Responsive navigation chrome for the logged-in app. Renders three
 * presentations driven by Tailwind visibility classes (no viewport JS):
 *
 * - `lg` and up: 260px fixed sidebar on the left (full content).
 * - `md`: 64px icon-only rail on the left.
 * - below `md`: sidebar hidden; fixed top bar hosts a hamburger that opens a
 *   left-slide drawer containing the full sidebar content.
 *
 * Owns search-modal (Cmd/Ctrl+K) and drawer open state; closes the drawer on
 * route change.
 *
 * **Why CSS visibility instead of `useBreakpoint` JS gating?**
 * All three trees mount at every viewport and Tailwind's `hidden md:flex
 * lg:hidden`-style classes hide the inactive ones. Costs ~30–40 hidden DOM
 * nodes (`display:none`, not painted, not in layout) — well below anything
 * worth optimizing. In return:
 * - State is preserved across resize for most subtrees (e.g. the search
 *   modal). JS gating would unmount the inactive variants and require
 *   `useEffect`s to reconcile stale state against unmounted views.
 * - First paint is correct without a window-size read. There's no SSR in
 *   this app, so flash isn't the threat — the savings are robustness, not
 *   pixels.
 * If we ever embed something expensive (charts, virtualized lists) inside
 * one of the variants, revisit and gate that subtree on `useBreakpoint`.
 * The shell stays cheap.
 *
 * **Drawer is the one exception** to "state preserved across resize":
 * `MobileNavDrawer` is `lg:hidden` (visible at both `<md` and `md`) — the
 * mobile top bar's hamburger opens it at `<md`, and the icon rail's top
 * hamburger opens it at `md`. At `lg+` the drawer's overlay is
 * CSS-invisible but `react-aria-components`' `Modal` still mounts the
 * dialog and activates a focus trap when `isOpen` is true. The hamburgers
 * that open it are both also gone at `lg+` so a fresh open at `lg+` is
 * impossible, but stale `isDrawerOpen=true` across the resize boundary
 * is not. We watch the `lg` breakpoint and force-close when it crosses
 * true.
 */
export const BaseSidebar = ({
  user,
  activeUrl,
  items,
  footerItems = [],
  showAccountCard = true,
  showAlertsBell = false,
  alertsUnreadCount = 0,
  isAlertsCenterOpen = false,
  navigationKey,
  onOpenAlerts,
  className,
}: IBaseSidebarProps) => {
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const isLgUp = useBreakpoint('lg');

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Any navigation closes the drawer — a new page, or the same page re-entered
  // with new history state (the assistant sheet opening from "Ask Mantle").
  React.useEffect(() => {
    setIsDrawerOpen(false);
  }, [activeUrl, navigationKey]);

  // Defensive: close the drawer if the viewport crosses to `lg+` while it's
  // open. The overlay is `lg:hidden` (CSS-invisible at `lg+`) but the
  // underlying react-aria modal would still keyboard-trap focus. See JSDoc
  // above.
  React.useEffect(() => {
    if (isLgUp) {
      setIsDrawerOpen(false);
    }
  }, [isLgUp]);

  const openSearch = React.useCallback(() => setIsSearchOpen(true), []);
  const openDrawer = React.useCallback(() => setIsDrawerOpen(true), []);

  // The drawer closes on a route change, but the bell pressed from `/assistant`
  // stays on `/assistant` — so it closes the drawer itself, or the center opens
  // behind the modal and its focus trap.
  const openAlertsAndCloseDrawer = React.useCallback(() => {
    setIsDrawerOpen(false);
    onOpenAlerts?.();
  }, [onOpenAlerts]);

  const alertsBellProps = {
    showAlertsBell,
    alertsUnreadCount,
    isAlertsCurrent: isAlertsCenterOpen || activeUrl === PATHS.ALERTS,
    onOpenAlerts: onOpenAlerts ? openAlertsAndCloseDrawer : undefined,
  };

  return (
    <>
      {/* Desktop fixed sidebar (lg+) */}
      <div
        style={{ '--width': `${MAIN_SIDEBAR_WIDTH_PX}px` } as React.CSSProperties}
        className={cx(
          'lg:w-(--width) bg-app shadow-nav-edge fixed inset-y-0 left-0 z-30 hidden max-w-full lg:flex',
          className
        )}
      >
        <FullSidebarContent
          user={user}
          activeUrl={activeUrl}
          items={items}
          footerItems={footerItems}
          showAccountCard={showAccountCard}
          onOpenSearch={openSearch}
          {...alertsBellProps}
        />
      </div>

      {/* Tablet icon rail (md only) */}
      <IconRailSidebar
        user={user}
        activeUrl={activeUrl}
        items={items}
        onOpenDrawer={openDrawer}
        className="bg-app shadow-nav-edge fixed inset-y-0 left-0 z-30 hidden md:flex lg:hidden"
        {...alertsBellProps}
      />

      {/* Mobile (<md) top bar + drawer */}
      <MobileTopBar onOpenDrawer={openDrawer} className="md:hidden" {...alertsBellProps} />
      <MobileNavDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        user={user}
        activeUrl={activeUrl}
        items={items}
        footerItems={footerItems}
        showAccountCard={showAccountCard}
        onOpenSearch={openSearch}
        {...alertsBellProps}
      />

      <EntitySearchModal isOpen={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </>
  );
};

/**
 * App-wired `MainSidebar` used by `HomeScene`. Pulls the authenticated user
 * and active route from context and composes the nav item list (dashboard,
 * accounts, spending, recurring) before delegating to `BaseSidebar`.
 *
 * Two consumer features ride on feature gates:
 * - "Bills & Subscriptions" entry — `feat-obligations`.
 * - Alerts bell + unread pip — `feat-alerts-center`. Rendered by each sidebar
 *   variant and the mobile top bar via `NotificationBellButton`.
 *
 * The unread count is read here rather than inside the bell because all three
 * sidebar presentations mount at every viewport (inactive ones hidden by CSS),
 * so a self-fetching bell would open one query subscription per presentation.
 * It polls because the chrome never unmounts: without a cadence the count
 * would only move on a page load or a mutation the viewer performed
 * themselves.
 *
 * Internal debug utilities (Sentry test, Environment) live in the avatar
 * dropdown's `Debug` submenu gated on `debug-tools` ([ORN-691]).
 */
const MainSidebar = () => {
  const { user, loading } = useAuthState();
  const location = useLocation();
  const addDateParamsToPath = usePathWithDateRange();
  const activeUrl = location.pathname;
  const showBillsSubscriptions = useFeatureGate(FeatureGate.Obligations);
  const showAlertsBell = useFeatureGate(FeatureGate.AlertsCenter);
  const { isOpen, alertsPresentation, assistantMountSite, toggleAlertsCenter, showAlerts } = useSidePanel();
  const navigate = useNavigate();

  // At the column the bell toggles the full center. On the sheet it opens the
  // sheet onto the center — the sheet covers the top bar, so the bell is only
  // ever pressed while the sheet is closed. On the route the pane is
  // `/assistant`, so the bell opens the center and goes there.
  const openAlerts = React.useCallback(() => {
    if (assistantMountSite === 'column') {
      toggleAlertsCenter();
      return;
    }
    showAlerts();
    if (assistantMountSite === 'route') {
      void navigate(PATHS.ASSISTANT);
    }
  }, [assistantMountSite, toggleAlertsCenter, showAlerts, navigate]);

  // Filterless, like the Alerts Center's own counts read, so the two share one
  // cache entry and cannot report different numbers. The chrome never unmounts,
  // so without the poll the pip would freeze at its login-time number.
  const { counts: workItemCounts } = useWorkItemStatusCounts({
    skip: !showAlertsBell,
    pollInterval: ALERT_COUNT_POLL_INTERVAL_MS,
  });

  const alertsUnreadCount = workItemCounts.unread;

  if (loading) {
    return null;
  }

  if (!user) {
    return null;
  }

  const rawItems: NavItemType[] = [
    {
      label: 'Home',
      href: PATHS.DASHBOARD,
      icon: HomeIcon,
    },
    {
      label: 'Accounts',
      href: PATHS.ACCOUNTS,
      icon: BarChartIcon,
    },
    {
      label: 'Spending',
      href: PATHS.SPENDING(),
      icon: BankNoteIcon,
    },
    ...(showBillsSubscriptions
      ? [
          {
            label: 'Bills & Subscriptions',
            // The full label overflows the 64px icon rail and gets clipped
            // off-screen; the rail uses this shorter form instead. Full
            // sidebar + mobile drawer continue to show the long label.
            iconRailLabel: 'Bills',
            href: PATHS.OBLIGATIONS,
            icon: CalendarIcon,
          },
        ]
      : []),
    {
      label: 'Recurring',
      href: PATHS.RECURRING_TRANSACTIONS,
      icon: CalendarIcon,
    },
  ];

  const items: NavItemType[] = rawItems.map((item) => {
    if (item.items?.length) {
      return {
        ...item,
        href: item.href ? addDateParamsToPath(item.href) : undefined,
        items: item.items.map((subItem) => ({
          ...subItem,
          href: addDateParamsToPath(subItem.href),
        })),
      };
    }
    return {
      ...item,
      href: item.href ? addDateParamsToPath(item.href) : undefined,
    };
  });

  return (
    <BaseSidebar
      items={items}
      user={user}
      activeUrl={activeUrl}
      showAlertsBell={showAlertsBell}
      alertsUnreadCount={alertsUnreadCount}
      // The site as well as the presentation: the column and the sheet are the
      // authority for "the center is on screen". On the route the presentation
      // outlives the pane's route, so it would keep the bell marked current
      // long after the user had navigated away.
      isAlertsCenterOpen={assistantMountSite !== 'route' && isOpen && alertsPresentation === 'full'}
      navigationKey={location.key}
      onOpenAlerts={openAlerts}
    />
  );
};

export default MainSidebar;
